import { useCallback, useEffect, useRef, useState } from 'react';

import {
  fetchCatalogoOficinaExterna,
  type CatalogoOficinaExterna,
} from '../api/oficinaExternaApi';
import {
  anexarAoAtendimentoExistente,
  reenviarItem,
  sincronizarFila,
} from '../infrastructure/offline/filaOficinaExterna';
import {
  lerAtendimentosLocais,
  lerCatalogo,
  lerFila,
  removerItemDaFila,
  salvarCatalogo,
  type AtendimentoLocal,
  type ItemDaFila,
} from '../infrastructure/offline/oficinaExternaDb';

/** Rede de segurança: mesmo sem evento nenhum, tenta subir a fila de tempos em tempos. */
const INTERVALO_DE_SYNC_MS = 60_000;

export type OrigemDoCatalogo = 'SERVIDOR' | 'LOCAL' | 'NENHUM';

export interface EstadoOffline {
  catalogo: CatalogoOficinaExterna | null;
  /** Quando o aparelho baixou estes dados — a tela MOSTRA isso, não é metadado interno. */
  baixadoEm: string | null;
  origem: OrigemDoCatalogo;
  carregando: boolean;
  /** Falhou a atualização E não havia nada gravado: a tela precisa dizer isso por extenso. */
  semDados: boolean;
  online: boolean;
  atendimentosLocais: AtendimentoLocal[];
  fila: ItemDaFila[];
  pendentes: number;
  recusados: number;
  atualizarCatalogo: () => Promise<void>;
  recarregarFila: () => Promise<void>;
  sincronizar: () => Promise<void>;
  reenviar: (clienteId: string) => Promise<void>;
  descartar: (clienteId: string) => Promise<void>;
  anexarAoExistente: (clienteIdDoCadastro: string, idServidor: string) => Promise<void>;
}

/**
 * O estado offline das telas do inspetor (at-114, ADR 0059).
 *
 * 🔴 Sem rede, o catálogo gravado É a resposta — e a tela precisa saber DE QUANDO ele é. Por isso
 * `origem` e `baixadoEm` são parte do estado, não detalhe: "estes dados são de hoje às 08:12" e
 * "não consegui atualizar" levam o inspetor a agir de formas diferentes.
 *
 * 🔴 `semDados` é distinto de "catálogo vazio": um aparelho novo, que nunca baixou nada e está sem
 * rede, NÃO pode mostrar a árvore vazia — isso seria lido como "não há nada para visitar".
 */
export const useOficinaExternaOffline = (): EstadoOffline => {
  const [catalogo, setCatalogo] = useState<CatalogoOficinaExterna | null>(null);
  const [baixadoEm, setBaixadoEm] = useState<string | null>(null);
  const [origem, setOrigem] = useState<OrigemDoCatalogo>('NENHUM');
  const [carregando, setCarregando] = useState(true);
  const [semDados, setSemDados] = useState(false);
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  );
  const [atendimentosLocais, setAtendimentosLocais] = useState<AtendimentoLocal[]>([]);
  const [fila, setFila] = useState<ItemDaFila[]>([]);

  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const recarregarFila = useCallback(async () => {
    try {
      const [itens, locais] = await Promise.all([lerFila(), lerAtendimentosLocais()]);
      if (!montado.current) return;
      setFila(itens);
      setAtendimentosLocais(locais);
    } catch {
      // Silêncio deliberado: a indisponibilidade do armazenamento já é anunciada na gravação
      // (que é onde o inspetor perde trabalho). Aqui, repetir o aviso a cada ciclo de 60s só
      // treinaria a pessoa a ignorá-lo.
    }
  }, []);

  const atualizarCatalogo = useCallback(async () => {
    setCarregando(true);
    try {
      const dados = await fetchCatalogoOficinaExterna();
      const registro = await salvarCatalogo(dados).catch(() => null);
      if (!montado.current) return;
      setCatalogo(dados);
      setBaixadoEm(registro?.baixadoEm ?? new Date().toISOString());
      setOrigem('SERVIDOR');
      setSemDados(false);
    } catch {
      // Sem rede (ou servidor fora): cai no que estiver gravado. A tela continua utilizável —
      // é o ponto inteiro do offline —, dizendo de quando os dados são.
      const local = await lerCatalogo().catch(() => null);
      if (!montado.current) return;
      if (local) {
        setCatalogo(local.dados);
        setBaixadoEm(local.baixadoEm);
        setOrigem('LOCAL');
        setSemDados(false);
      } else {
        setCatalogo(null);
        setBaixadoEm(null);
        setOrigem('NENHUM');
        setSemDados(true);
      }
    } finally {
      if (montado.current) setCarregando(false);
    }
  }, []);

  const sincronizar = useCallback(async () => {
    try {
      await sincronizarFila();
    } finally {
      await recarregarFila();
    }
  }, [recarregarFila]);

  // Carga inicial: primeiro o que está no aparelho (instantâneo, funciona sem rede), depois a
  // atualização. Esperar a rede para desenhar deixaria a tela em branco no pátio da oficina.
  useEffect(() => {
    void (async () => {
      const local = await lerCatalogo().catch(() => null);
      if (local && montado.current) {
        setCatalogo(local.dados);
        setBaixadoEm(local.baixadoEm);
        setOrigem('LOCAL');
        setCarregando(false);
      }
      await recarregarFila();
      await atualizarCatalogo();
      await sincronizar();
    })();
  }, [atualizarCatalogo, recarregarFila, sincronizar]);

  // Gatilhos de sincronização: voltar a rede, voltar à aba e o intervalo de segurança.
  useEffect(() => {
    const aoVoltarRede = () => {
      setOnline(true);
      void sincronizar();
      void atualizarCatalogo();
    };
    const aoPerderRede = () => setOnline(false);
    const aoVoltarAAba = () => {
      if (document.visibilityState === 'visible') void sincronizar();
    };

    window.addEventListener('online', aoVoltarRede);
    window.addEventListener('offline', aoPerderRede);
    document.addEventListener('visibilitychange', aoVoltarAAba);
    const intervalo = window.setInterval(() => {
      if (navigator.onLine !== false) void sincronizar();
    }, INTERVALO_DE_SYNC_MS);

    return () => {
      window.removeEventListener('online', aoVoltarRede);
      window.removeEventListener('offline', aoPerderRede);
      document.removeEventListener('visibilitychange', aoVoltarAAba);
      window.clearInterval(intervalo);
    };
  }, [atualizarCatalogo, sincronizar]);

  const reenviar = useCallback(
    async (clienteId: string) => {
      await reenviarItem(clienteId);
      await sincronizar();
    },
    [sincronizar],
  );

  const descartar = useCallback(
    async (clienteId: string) => {
      await removerItemDaFila(clienteId);
      await recarregarFila();
    },
    [recarregarFila],
  );

  const anexarAoExistente = useCallback(
    async (clienteIdDoCadastro: string, idServidor: string) => {
      await anexarAoAtendimentoExistente(clienteIdDoCadastro, idServidor);
      await sincronizar();
    },
    [sincronizar],
  );

  return {
    catalogo,
    baixadoEm,
    origem,
    carregando,
    semDados,
    online,
    atendimentosLocais,
    fila,
    pendentes: fila.filter((i) => i.estado === 'PENDENTE' || i.estado === 'ENVIANDO').length,
    recusados: fila.filter((i) => i.estado === 'RECUSADO').length,
    atualizarCatalogo,
    recarregarFila,
    sincronizar,
    reenviar,
    descartar,
    anexarAoExistente,
  };
};
